/**
 * Core mortgage calculation utilities
 */

/**
 * Calculate monthly mortgage payment (Principal + Interest only)
 * @param {number} principal - Loan amount
 * @param {number} annualRate - Annual interest rate (as percentage, e.g., 6.5)
 * @param {number} years - Loan term in years
 * @returns {number} Monthly payment
 */
export function calculateMonthlyPayment(principal, annualRate, years) {
    if (principal <= 0 || years <= 0) return 0;
    if (annualRate === 0) return principal / (years * 12);

    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;

    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);

    return payment;
}

/**
 * Calculate interest rate adjustments for buydown options
 * @param {string} buydownType - Type of buydown (none, 1-0, 2-1, 3-2-1)
 * @param {number} baseRate - Base interest rate
 * @param {number} year - Current year (1-indexed)
 * @returns {number} Adjusted interest rate for the year
 */
export function getBuydownRate(buydownType, baseRate, year) {
    if (buydownType === 'none') return baseRate;

    const adjustments = {
        '1-0': [1, 0],
        '2-1': [2, 1, 0],
        '3-2-1': [3, 2, 1, 0]
    };

    const adjustment = adjustments[buydownType];
    if (!adjustment) return baseRate;

    const index = year - 1;
    const reduction = index < adjustment.length ? adjustment[index] : 0;

    return Math.max(0, baseRate - reduction);
}

/**
 * Calculate buydown payment schedule
 * @param {number} principal - Loan amount
 * @param {number} baseRate - Base interest rate
 * @param {number} years - Total loan term
 * @param {string} buydownType - Type of buydown
 * @returns {Array} Array of payment periods with rates and payments
 */
export function calculateBuydownSchedule(principal, baseRate, years, buydownType) {
    if (buydownType === 'none') {
        return [{
            period: `All ${years} years`,
            rate: baseRate,
            payment: calculateMonthlyPayment(principal, baseRate, years)
        }];
    }

    const schedule = [];
    const buydownYears = {
        '1-0': 1,
        '2-1': 2,
        '3-2-1': 3
    }[buydownType] || 0;

    for (let year = 1; year <= buydownYears; year++) {
        const rate = getBuydownRate(buydownType, baseRate, year);
        const payment = calculateMonthlyPayment(principal, rate, years);
        schedule.push({
            period: `Year ${year}`,
            rate,
            payment
        });
    }

    const remainingRate = baseRate;
    const remainingPayment = calculateMonthlyPayment(principal, remainingRate, years);
    schedule.push({
        period: `Years ${buydownYears + 1}-${years}`,
        rate: remainingRate,
        payment: remainingPayment
    });

    return schedule;
}

/**
 * Generate complete amortization schedule
 * @param {number} principal - Loan amount
 * @param {number} annualRate - Annual interest rate
 * @param {number} years - Loan term
 * @param {string} loanType - Type of loan (for ARM highlighting)
 * @param {number} homePrice - Home price (for PMI calculation)
 * @param {number} downPaymentPercent - Down payment percentage (for PMI calculation)
 * @param {number} monthlyPMI - Optional: Monthly PMI amount (if not provided, will be calculated)
 * @returns {Array} Array of payment details for each month
 */
export function generateAmortizationSchedule(principal, annualRate, years, loanType = '30-year-fixed', homePrice = 0, downPaymentPercent = 20, monthlyPMI = null, extraPayment = 0) {
    const schedule = [];
    const monthlyPayment = calculateMonthlyPayment(principal, annualRate, years);
    const monthlyRate = annualRate / 100 / 12;
    let balance = principal;

    // Calculate fixed monthly PMI amount - use provided value or calculate
    const fixedMonthlyPMI = monthlyPMI !== null ? monthlyPMI : calculatePMI(principal, homePrice, downPaymentPercent);
    const pmiCutoffBalance = homePrice * 0.8;

    const armYears = {
        '5-1-arm': 5,
        '7-1-arm': 7,
        '10-1-arm': 10
    };

    const armFixedMonths = armYears[loanType] ? armYears[loanType] * 12 : null;
    const maxMonths = years * 12;

    for (let month = 1; month <= maxMonths; month++) {
        // Stop if loan is fully paid
        if (balance <= 0) break;

        const interestPayment = balance * monthlyRate;
        // Standard principal payment part
        let principalPayment = monthlyPayment - interestPayment;

        // Apply extra payment
        principalPayment += extraPayment;

        // Cap payment at remaining balance
        if (principalPayment > balance) {
            principalPayment = balance;
        }

        // Determine if PMI applies this month
        // PMI is removed when balance drops below 80% of original home value
        const currentPMI = (balance > pmiCutoffBalance) ? fixedMonthlyPMI : 0;

        balance -= principalPayment;

        // Prevent negative balance due to floating point
        if (balance < 0.01) balance = 0;

        const isArmWarning = armFixedMonths && month > armFixedMonths;

        schedule.push({
            month,
            year: Math.ceil(month / 12),
            payment: principalPayment + interestPayment + currentPMI, // Total payment for this month including PMI
            principal: principalPayment,
            interest: interestPayment,
            pmi: currentPMI,
            balance: balance,
            isArmWarning
        });
    }

    return schedule;
}

/**
 * Calculate PMI (Private Mortgage Insurance)
 * @param {number} loanAmount - Loan amount
 * @param {number} homePrice - Home price
 * @param {number} downPaymentPercent - Down payment as percentage
 * @returns {number} Monthly PMI amount
 */
export function calculatePMI(loanAmount, homePrice, downPaymentPercent) {
    if (downPaymentPercent >= 20) return 0;

    // PMI typically ranges from 0.5% to 1% of loan amount annually
    // Using 0.75% as average
    const pmiRate = 0.0075;
    const annualPMI = loanAmount * pmiRate;

    return annualPMI / 12;
}

/**
 * Calculate when PMI will be removed (when LTV reaches 80%)
 * @param {number} principal - Original loan amount
 * @param {number} homePrice - Home price
 * @param {number} annualRate - Interest rate
 * @param {number} years - Loan term
 * @param {number} extraPayment - Extra monthly principal payment
 * @returns {object} PMI removal details
 */
export function calculatePMIRemoval(principal, homePrice, annualRate, years, extraPayment = 0) {
    const targetBalance = homePrice * 0.8;
    const monthlyPayment = calculateMonthlyPayment(principal, annualRate, years);
    const monthlyRate = annualRate / 100 / 12;
    let balance = principal;
    let month = 0;

    while (balance > targetBalance && month < years * 12) {
        month++;
        const interestPayment = balance * monthlyRate;
        const principalPayment = monthlyPayment - interestPayment + extraPayment;
        balance -= principalPayment;
    }

    return {
        months: month,
        years: (month / 12).toFixed(1),
        date: month > 0 ? `${Math.floor(month / 12)} years, ${month % 12} months` : 'N/A'
    };
}

/**
 * Calculate prepayment analysis
 * @param {number} principal - Loan amount
 * @param {number} annualRate - Interest rate
 * @param {number} years - Loan term
 * @param {number} extraPayment - Extra monthly payment
 * @param {number} homePrice - Home price (for PMI calculation)
 * @param {number} downPaymentPercent - Down payment percentage
 * @param {number} monthlyPMI - Optional: Monthly PMI amount (if not provided, will be calculated)
 * @returns {object} Comparison of original vs prepayment scenario
 */
export function calculatePrepaymentAnalysis(principal, annualRate, years, extraPayment, homePrice, downPaymentPercent, monthlyPMI = null) {
    // Original scenario
    const originalSchedule = generateAmortizationSchedule(principal, annualRate, years);
    const originalTotalInterest = originalSchedule.reduce((sum, payment) => sum + payment.interest, 0);

    // Use provided PMI or calculate it
    const pmiAmount = monthlyPMI !== null ? monthlyPMI : calculatePMI(principal, homePrice, downPaymentPercent);

    const originalPMIRemoval = calculatePMIRemoval(principal, homePrice, annualRate, years, 0);
    const originalTotalPMI = pmiAmount * originalPMIRemoval.months;

    // Prepayment scenario
    const monthlyPayment = calculateMonthlyPayment(principal, annualRate, years);
    const monthlyRate = annualRate / 100 / 12;
    let balance = principal;
    let month = 0;
    let totalInterest = 0;

    while (balance > 0 && month < years * 12) {
        month++;
        const interestPayment = balance * monthlyRate;
        const principalPayment = monthlyPayment - interestPayment + extraPayment;
        totalInterest += interestPayment;
        balance -= principalPayment;
        if (balance < 0) balance = 0;
    }

    const revisedPMIRemoval = calculatePMIRemoval(principal, homePrice, annualRate, years, extraPayment);
    const revisedTotalPMI = pmiAmount * revisedPMIRemoval.months;

    return {
        original: {
            months: years * 12,
            years: years,
            totalInterest: originalTotalInterest,
            interestPercent: (originalTotalInterest / principal * 100).toFixed(2),
            totalPMI: originalTotalPMI,
            pmiRemovalDate: originalPMIRemoval.date
        },
        revised: {
            months: month,
            years: (month / 12).toFixed(1),
            totalInterest: totalInterest,
            interestPercent: (totalInterest / principal * 100).toFixed(2),
            totalPMI: revisedTotalPMI,
            pmiRemovalDate: revisedPMIRemoval.date
        },
        savings: {
            months: years * 12 - month,
            years: ((years * 12 - month) / 12).toFixed(1),
            interest: originalTotalInterest - totalInterest,
            pmi: originalTotalPMI - revisedTotalPMI,
            total: (originalTotalInterest - totalInterest) + (originalTotalPMI - revisedTotalPMI)
        }
    };
}

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}

/**
 * Format percentage for display
 * @param {number} percent - Percentage to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export function formatPercent(percent, decimals = 2) {
    return `${percent.toFixed(decimals)}%`;
}
